import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaArrowRight, FaChevronDown } from 'react-icons/fa';
import logo from '../assets/logo.302f19bf0f68f79389f2.png'



const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSection, setActiveSection] = useState('home');

  // Menu items with their corresponding section IDs
  const menuItems = [
    { name: 'Home', href: '#home', id: 'home' },
    { name: 'Features', href: '#features', id: 'features' },
    { name: 'Solutions', href: '#solutions', id: 'solutions' },
    { name: 'Pricing', href: '#pricing', id: 'pricing' },
    { name: 'Resources', href: '#resources', id: 'resources' },
  ];

  // Scroll spy functionality
  useEffect(() => {
    const handleScroll = () => {
      // Navbar background change on scroll
      setIsScrolled(window.scrollY > 50);

      // Get all sections
      const sections = menuItems.map(item => document.getElementById(item.id));

      // Find which section is currently in view
      let currentSection = 'home';
      const scrollPosition = window.scrollY + 150; // Offset for navbar height

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;

          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            currentSection = menuItems[i].id;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once to set initial active section

    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuItems]);

  // Smooth scroll function
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const section = document.getElementById(sectionId);
    if (section) {
      const offset = 80; // Navbar height offset
      const elementPosition = section.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${isScrolled ? 'nav-blur shadow-2xl' : 'bg-transparent'
      }`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex justify-between items-center">

        <img
          src={logo}
          alt="logo"
          className="h-12 w-auto object-contain invert brightness-200"
        />

        {/* Desktop Menu with Active Highlight */}
        <div className="hidden md:flex items-center space-x-1">
          {menuItems.map((item, idx) => (
            <div key={idx} className="relative">
              <a
                href={item.href}
                onClick={(e) => scrollToSection(e, item.id)}
                className={`relative px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-1 ${activeSection === item.id
                    ? 'text-white font-semibold'
                    : 'text-gray-300 hover:text-white'
                  }`}
              >
                {item.name}
                {item.name === 'Solutions' && <FaChevronDown className="text-xs" />}

                {/* Active Indicator Line */}
                {activeSection === item.id && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full animate-pulse"></span>
                )}
              </a>

              {/* Solutions Dropdown */}
              {item.name === 'Solutions' && (
                <div className="absolute top-12 left-0 w-64 glass-card rounded-xl shadow-2xl p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Sales Teams
                  </a>
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Marketing Agencies
                  </a>
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Recruitment
                  </a>
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Enterprise
                  </a>
                </div>
              )}

              {/* Resources Dropdown */}
              {item.name === 'Resources' && (
                <div className="absolute top-12 left-0 w-64 glass-card rounded-xl shadow-2xl p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Blog
                  </a>
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Case Studies
                  </a>
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Documentation
                  </a>
                  <a href="#" className="block px-4 py-2.5 hover:bg-purple-500/20 rounded-lg text-sm transition-all duration-200 hover:translate-x-1">
                    Webinars
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <button className="text-gray-300 hover:text-white transition-all duration-300 px-4 py-2 rounded-full hover:bg-white/5">
            Login
          </button>
          <button className="btn-primary flex items-center gap-2 group">
            Sign Up Free
            <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-2xl p-2 hover:bg-white/10 rounded-lg transition-all duration-300"
          onClick={() => setMobileMenuOpen(true)}
        >
          <FaBars />
        </button>
      </div>

      {/* Mobile Sidebar with Active Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex animate-fade-in">
          <div
            className="bg-black/70 backdrop-blur-md w-full h-full transition-all duration-300"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          <div className="w-80 bg-gradient-to-b from-[#0F111A] to-[#0A0A0F] h-full shadow-2xl p-6 flex flex-col gap-3 animate-slide-right">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-white/10">
              <span className="text-2xl font-bold gradient-text">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all duration-300"
              >
                <FaTimes />
              </button>
            </div>

            {menuItems.map((item, idx) => (
              <a
                key={idx}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.id)}
                className={`py-3 px-4 rounded-xl transition-all duration-300 flex items-center justify-between ${activeSection === item.id
                    ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 text-white font-semibold border-l-4 border-purple-500'
                    : 'hover:bg-purple-500/20 hover:translate-x-2'
                  }`}
              >
                {item.name}
                {activeSection === item.id && (
                  <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                )}
              </a>
            ))}

            {/* Solutions Submenu in Mobile */}
            <div className="ml-4 mt-2 space-y-2 border-l-2 border-white/10 pl-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Solutions</p>
              <a href="#" className="block py-2 text-sm text-gray-400 hover:text-white transition">Sales Teams</a>
              <a href="#" className="block py-2 text-sm text-gray-400 hover:text-white transition">Marketing Agencies</a>
              <a href="#" className="block py-2 text-sm text-gray-400 hover:text-white transition">Recruitment</a>
            </div>

            {/* Resources Submenu in Mobile */}
            <div className="ml-4 mt-2 space-y-2 border-l-2 border-white/10 pl-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Resources</p>
              <a href="#" className="block py-2 text-sm text-gray-400 hover:text-white transition">Blog</a>
              <a href="#" className="block py-2 text-sm text-gray-400 hover:text-white transition">Case Studies</a>
              <a href="#" className="block py-2 text-sm text-gray-400 hover:text-white transition">Documentation</a>
            </div>

            <div className="flex flex-col gap-3 mt-6 pt-6 border-t border-white/10">
              <button className="border border-gray-600 py-3 rounded-xl hover:bg-white/5 transition-all duration-300">
                Login
              </button>
              <button className="bg-gradient-to-r from-purple-600 to-indigo-600 py-3 rounded-xl hover:shadow-lg transition-all duration-300">
                Sign Up Free
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;